/**
 * Hero "Edit Overlay — Text & Effects" baker — functional port of the legacy
 * canvas composer (__ovRecompose): kicker + headline + subtext + scrim
 * gradient + a themed light-on-dark effect photo screen-blended on top
 * (black drops out, only the glow shows). Renders at 2× as JPEG 0.92 so the
 * 600px display stays crisp. Output is ONE flattened data-URL image — the
 * hero render stays a single email-safe <img>; host before send.
 */

export const OVERLAY_FX: Record<string, string> = {
  fireworks: "https://images.pexels.com/photos/36234335/pexels-photo-36234335.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=627&w=1200",
  confetti: "https://images.pexels.com/photos/9665186/pexels-photo-9665186.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=627&w=1200",
  sparkle: "https://images.pexels.com/photos/237898/pexels-photo-237898.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=627&w=1200",
  snow: "https://images.pexels.com/photos/7245519/pexels-photo-7245519.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=627&w=1200",
};

export interface OverlaySpec {
  kicker: string;
  head: string;
  sub: string;
  col: string;
  align: "left" | "center";
  v: "top" | "middle" | "bottom";
  /** headline px at 600w */
  size: number;
  /** scrim strength 0-80 (%) */
  scrim: number;
  fx: string; // "none" | key of OVERLAY_FX
}

function loadImg(url: string): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => res(im);
    im.onerror = () => rej(new Error("image load failed: " + url));
    im.src = url;
  });
}

export async function bakeOverlay(srcUrl: string, spec: OverlaySpec): Promise<string> {
  const base = await loadImg(srcUrl);
  const W = 1200; // 2× the 600px email column
  const H = Math.round((base.naturalHeight / base.naturalWidth) * W) || 712;
  const cv = document.createElement("canvas");
  cv.width = W; cv.height = H;
  const ctx = cv.getContext("2d")!;
  ctx.drawImage(base, 0, 0, W, H);

  /* scrim gradient — anchored to the text position */
  if (spec.scrim > 0) {
    const a = spec.scrim / 100;
    const g = spec.v === "top" ? ctx.createLinearGradient(0, 0, 0, H * 0.7) : ctx.createLinearGradient(0, H, 0, H * 0.3);
    g.addColorStop(0, `rgba(10,18,35,${a})`);
    g.addColorStop(1, "rgba(10,18,35,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
  }

  /* effect photo — screen blend so black drops out */
  if (spec.fx && spec.fx !== "none" && OVERLAY_FX[spec.fx]) {
    try {
      const fx = await loadImg(OVERLAY_FX[spec.fx]);
      ctx.globalCompositeOperation = "screen";
      ctx.drawImage(fx, 0, 0, W, H);
      ctx.globalCompositeOperation = "source-over";
    } catch { /* effect unreachable — bake text only */ }
  }

  /* text */
  const scale = 2;
  const pad = 40 * scale;
  const cxp = spec.align === "center" ? W / 2 : pad;
  ctx.textAlign = spec.align === "center" ? "center" : "left";
  ctx.fillStyle = spec.col || "#ffffff";
  const headPx = (spec.size || 40) * scale;
  const lines: { txt: string; font: string; gap: number }[] = [];
  if (spec.kicker) lines.push({ txt: spec.kicker.toUpperCase(), font: `800 ${13 * scale}px Arial`, gap: 10 * scale });
  if (spec.head) lines.push({ txt: spec.head, font: `900 ${headPx}px 'Arial Black',Arial`, gap: 12 * scale });
  if (spec.sub) lines.push({ txt: spec.sub, font: `600 ${15 * scale}px Arial`, gap: 0 });
  const totalH = lines.reduce((s, l, i) => s + parseInt(l.font.match(/(\d+)px/)![1], 10) + (i < lines.length - 1 ? l.gap : 0), 0);
  let y = spec.v === "top" ? pad + parseInt(lines[0]?.font.match(/(\d+)px/)?.[1] || "0", 10) : spec.v === "middle" ? (H - totalH) / 2 + (lines.length ? parseInt(lines[0].font.match(/(\d+)px/)![1], 10) : 0) : H - pad - totalH + (lines.length ? parseInt(lines[0].font.match(/(\d+)px/)![1], 10) : 0);
  for (const l of lines) {
    ctx.font = l.font;
    ctx.shadowColor = "rgba(0,0,0,.35)"; ctx.shadowBlur = 8 * scale;
    ctx.fillText(l.txt, cxp, y);
    ctx.shadowBlur = 0;
    const px = parseInt(l.font.match(/(\d+)px/)![1], 10);
    y += px * 0.35 + l.gap + px * 0.65;
  }
  return cv.toDataURL("image/jpeg", 0.92);
}
